import * as XLSX from 'xlsx-js-style';
import { CellObject, ExcelDataType, WorkSheet } from 'xlsx-js-style';
import { TRvdGetResponse } from 'shared/types/rvd.types';
import { notification } from 'antd';
import { TOvertimeGetByIdResponse } from 'shared/types/overtime.types';
import { prepareDataForOvertimeLoading, prepareDataForRvdLoading } from '../utils';
import {
  COLOR_LINE,
  COLOR_LINE_OVERTIME,
  COLOR_NUMBER_OVERTIME,
  CONTROLLERS_STRING,
  CONTROLLERS_STRING_OVERTIME,
  LITTLE_TOP_DATA,
  LITTLE_TOP_DATA_OVERTIME,
  MAIN_FONT,
  MAIN_TABLE_HEADERS,
  MAIN_TABLE_HEADERS_OVERTIME,
  RESPONSIBLES_STRING,
  SIGNER_AREA_HINTS,
  SIGNER_AREA_HINTS_OVERTIME,
  SIGNER_AREA_UNDERLINES,
  SIGNER_AREA_UNDERLINES_OVERTIME,
  TOP_DATA,
  TOP_DATA_OVERTIME,
  charsIterate,
  createDinamicStyles,
  createStyleObject,
  innerProperties,
  innerPropertiesCopy,
  patternOvertime,
  patternRvd,
  types,
  typesOvertime,
} from './consts';

export interface IPrepareCells {
  user: (string | number)[];
  rowIndex: number;
  worksheet: WorkSheet;
  types: XLSX.ExcelDataType[];
}

function fillVoidFields(worksheet: WorkSheet, rowLimit: number) {
  for (let row = 0; row < rowLimit; row++) {
    charsIterate('A', 'U', letter => {
      if (!worksheet[letter + row]) {
        worksheet[letter + row] = { v: '' };
      }
    });
  }
}

function prepareCells(type: ExcelDataType, value: string | number, address: { c: number; r: number }): CellObject {
  return {
    t: type, // тип
    v: value, // значение
    r: `${String.fromCharCode(65 + address.c)}${address.r + 1}`, // адрес ячейки
  };
}

function prepareUserData(prepareInfo: IPrepareCells) {
  const { rowIndex, types, user, worksheet } = prepareInfo;
  for (let col = 0; col < 15; col++) {
    worksheet[String.fromCharCode(65 + col) + rowIndex] = prepareCells(types[col], user[col], { c: col, r: rowIndex });
  }
}

export const downloadExcell = (rvd: TRvdGetResponse | undefined) => {
  if (!rvd) {
    notification.error({ message: 'Отсутствуют данные для загрузки' });
    return;
  }
  const data = prepareDataForRvdLoading(rvd);
  const docSignatory = rvd.docSignatory;
  const workbook = XLSX.utils.book_new();
  const ws = patternRvd;
  const newWs: WorkSheet = {};

  const rowsToAdd = data.length;
  const limits: [string, string] = ws['!ref']!.split(':') as [string, string];
  const rowLimit: number = Number(limits[1].match(/\d+/)) + rowsToAdd;
  ws['!ref'] = `A1:U${rowLimit}`;

  innerPropertiesCopy.forEach(prop => {
    newWs[prop] = ws[prop];
  });
  fillVoidFields(ws, rowLimit);

  let controllersData = {};
  let responsiblesData = {};
  Object.keys(ws).forEach(key => {
    if (!innerProperties.includes(key)) {
      const row = key.match(/\d+/);
      if (!row) {
        return;
      }
      ws[key].s = {
        font: {
          name: MAIN_FONT,
          sz: 11,
        },
        alignment: {
          horizontal: 'center',
        },
        ...ws[key].s,
      };

      if (TOP_DATA[key]) {
        ws[key].s = { ...ws[key].s, ...TOP_DATA[key] };
      } else if (LITTLE_TOP_DATA[key]) {
        ws[key].s = { ...ws[key].s, ...LITTLE_TOP_DATA[key] };
      } else if (SIGNER_AREA_UNDERLINES[key]) {
        ws[key].s = { ...ws[key].s, ...SIGNER_AREA_UNDERLINES[key] };
      } else if (SIGNER_AREA_HINTS[key]) {
        ws[key].s = { ...ws[key].s, ...SIGNER_AREA_HINTS[key] };
      } else if (COLOR_LINE[key]) {
        ws[key].s = { ...ws[key].s, ...COLOR_LINE[key] };
      } else if (MAIN_TABLE_HEADERS[key]) {
        ws[key].s = { ...ws[key].s, ...MAIN_TABLE_HEADERS[key] };
      }

      if (
        ws[key].v === '(должность)' ||
        ws[key].v === '(подпись)' ||
        ws[key].v === '(ФИО)' ||
        ws[key].v === '(дата)' ||
        ws[key].v === '(фамилия, инициалы)'
      ) {
        ws[key].s = {
          ...ws[key].s,

          font: {
            name: MAIN_FONT,
            sz: 8,
            italic: true,
          },
        };
      }

      if (typeof ws[key].v === 'string' && ws[key].v.includes(CONTROLLERS_STRING)) {
        controllersData = createStyleObject(
          +row,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'H',
          'U',
        );
        const controllersCell = createStyleObject(
          +row,
          {
            font: {
              name: MAIN_FONT,
              sz: 10,
            },
          },
          'A',
          'A',
        );
        const controllersHeader = createStyleObject(
          +row + 1,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
          },
          'H',
          'U',
        );
        createDinamicStyles(controllersData, ws);
        createDinamicStyles(controllersHeader, ws);
        createDinamicStyles(controllersCell, ws);
      }
      if (typeof ws[key].v === 'string' && ws[key].v.includes(RESPONSIBLES_STRING)) {
        responsiblesData = createStyleObject(
          +row,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'H',
          'U',
        );
        const responsiblesHeader = createStyleObject(
          +row + 1,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
          },
          'H',
          'U',
        );
        const responsiblesCell = createStyleObject(
          +row,
          {
            font: {
              name: MAIN_FONT,
              sz: 10,
            },
          },
          'A',
          'A',
        );
        const signerData = createStyleObject(
          +row + 3,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'G',
          'M',
        );

        const opinionData = createStyleObject(
          +row + 7,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'A',
          'H',
        );

        const firstVisa = createStyleObject(
          +row + 10,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'B',
          'I',
        );

        const secondVisa = createStyleObject(
          +row + 12,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'B',
          'I',
        );

        const thirdVisa = createStyleObject(
          +row + 14,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'B',
          'I',
        );

        createDinamicStyles(responsiblesData, ws);
        createDinamicStyles(responsiblesHeader, ws);
        createDinamicStyles(responsiblesCell, ws);
        createDinamicStyles(signerData, ws);
        createDinamicStyles(opinionData, ws);
        createDinamicStyles(firstVisa, ws);
        createDinamicStyles(secondVisa, ws);
        createDinamicStyles(thirdVisa, ws);
      }
    }
  });

  let rowIndex = 19;
  const merges: XLSX.Range[] | undefined = [];
  ws['!merges']?.forEach(elem => {
    if (elem.s.r < rowIndex) {
      merges.push(elem);
    }
  });

  for (let row = 0; row < rowIndex; row++) {
    charsIterate('A', 'U', letter => {
      const cellAddress = letter + row;
      newWs[cellAddress] = ws[cellAddress];
    });
  }

  for (let row = rowIndex; row < rowLimit; row++) {
    charsIterate('A', 'U', letter => {
      const cellAddress = letter + row;
      const newCellAddress = `${letter}${row + rowsToAdd}`;
      newWs[newCellAddress] = ws[cellAddress];
    });

    if (row >= rowIndex) {
      ws['!merges']?.forEach(elem => {
        if (elem.s.r === row || elem.e.r === row) {
          const newElem = { s: { c: elem.s.c, r: row + rowsToAdd }, e: { c: elem.e.c, r: row + rowsToAdd } };
          merges.push(newElem);
        }
      });
    }
  }

  newWs['!merges'] = merges;

  for (let user = 0; user < data.length; user++) {
    // Добавляем новую ячейку в новые данные
    prepareUserData({ user: data[user], rowIndex: rowIndex, worksheet: newWs, types });
    rowIndex++;
  }
  newWs.D3 = { ...newWs.D3, v: docSignatory };
  XLSX.utils.book_append_sheet(workbook, newWs);
  XLSX.writeFile(workbook, 'updated_file.xlsx', { bookType: 'xlsx', bookSST: false, cellStyles: true });
};

export const downloadExcellOvertime = (overtime: TOvertimeGetByIdResponse | undefined) => {
  if (!overtime) {
    notification.error({ message: 'Отсутствуют данные для загрузки' });
    return;
  }
  const data = prepareDataForOvertimeLoading(overtime);
  const docSignatory = overtime.docSignatory;
  const workbook = XLSX.utils.book_new();
  const ws = patternOvertime;
  const newWs: WorkSheet = {};
  const rowsToAdd = data.length;
  const limits: [string, string] = ws['!ref']!.split(':') as [string, string];
  const rowLimit: number = +limits[1].match(/\d+/)! + rowsToAdd;
  ws['!ref'] = `A1:Q${rowLimit}`;

  innerPropertiesCopy.forEach(prop => {
    newWs[prop] = ws[prop];
  });
  fillVoidFields(ws, rowLimit);

  let controllersData = {};
  let responsiblesData = {};
  Object.keys(ws).forEach(key => {
    if (!innerProperties.includes(key)) {
      const row = key.match(/\d+/);
      if (!row) {
        return;
      }
      ws[key].s = {
        font: {
          name: MAIN_FONT,
          sz: 11,
        },
        alignment: {
          horizontal: 'center',
        },
        ...ws[key].s,
      };

      if (TOP_DATA[key]) {
        ws[key].s = { ...ws[key].s, ...TOP_DATA_OVERTIME[key] };
      } else if (LITTLE_TOP_DATA_OVERTIME[key]) {
        ws[key].s = { ...ws[key].s, ...LITTLE_TOP_DATA_OVERTIME[key] };
      } else if (SIGNER_AREA_UNDERLINES_OVERTIME[key]) {
        ws[key].s = {
          ...ws[key].s,
          ...SIGNER_AREA_UNDERLINES_OVERTIME[key],
          font: {
            name: MAIN_FONT,
            sz: 8,
          },
        };
      } else if (SIGNER_AREA_HINTS_OVERTIME[key]) {
        ws[key].s = { ...ws[key].s, ...SIGNER_AREA_HINTS_OVERTIME[key] };
      } else if (COLOR_NUMBER_OVERTIME[key]) {
        ws[key].s = { ...ws[key].s, ...COLOR_NUMBER_OVERTIME[key] };
      } else if (COLOR_LINE_OVERTIME[key]) {
        ws[key].s = { ...ws[key].s, ...COLOR_LINE_OVERTIME[key] };
      } else if (MAIN_TABLE_HEADERS_OVERTIME[key]) {
        ws[key].s = { ...ws[key].s, ...MAIN_TABLE_HEADERS_OVERTIME[key] };
      }

      if (
        ws[key].v === '(должность)' ||
        ws[key].v === '(подпись)' ||
        ws[key].v === '(ФИО)' ||
        ws[key].v === '(дата)' ||
        ws[key].v === '(фамилия, инициалы)'
      ) {
        ws[key].s = {
          ...ws[key].s,

          font: {
            name: MAIN_FONT,
            sz: 8,
            italic: true,
          },
        };
      }

      if (typeof ws[key].v === 'string' && ws[key].v.includes(CONTROLLERS_STRING_OVERTIME)) {
        controllersData = createStyleObject(
          +row,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'G',
          'Q',
        );
        const controllersCell = createStyleObject(
          +row,
          {
            font: {
              name: MAIN_FONT,
              sz: 10,
            },
          },
          'A',
          'A',
        );
        const controllersHeader = createStyleObject(
          +row + 1,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
          },
          'G',
          'Q',
        );
        const optionalControllersHeader = createStyleObject(
          +row + 1,
          {
            fill: {
              fgColor: {
                rgb: 'D9D9D9',
              },
            },
          },
          'J',
          'Q',
        );
        createDinamicStyles(controllersData, ws);
        createDinamicStyles(controllersHeader, ws);
        createDinamicStyles(optionalControllersHeader, ws);
        createDinamicStyles(controllersCell, ws);
      }
      if (typeof ws[key].v === 'string' && ws[key].v.includes(RESPONSIBLES_STRING)) {
        responsiblesData = createStyleObject(
          +row,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'G',
          'Q',
        );
        const responsiblesHeader = createStyleObject(
          +row + 1,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
          },
          'H',
          'Q',
        );
        const optionalResponsiblesHeader = createStyleObject(
          +row + 1,
          {
            fill: {
              fgColor: {
                rgb: 'D9D9D9',
              },
            },
          },
          'J',
          'Q',
        );
        const responsiblesCell = createStyleObject(
          +row,
          {
            font: {
              name: MAIN_FONT,
              sz: 10,
            },
          },
          'A',
          'A',
        );
        const signerData = createStyleObject(
          +row + 4,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'E',
          'I',
        );

        const signerDataFill = createStyleObject(
          +row + 5,
          {
            fill: {
              fgColor: {
                rgb: 'D9D9D9',
              },
            },
          },
          'E',
          'I',
        );

        const opinionData = createStyleObject(
          +row + 8,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'B',
          'I',
        );

        const firstVisa = createStyleObject(
          +row + 11,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'B',
          'I',
        );

        const firstVisaFill = createStyleObject(
          +row + 12,
          {
            fill: {
              fgColor: {
                rgb: 'D9D9D9',
              },
            },
          },
          'B',
          'I',
        );

        const secondVisa = createStyleObject(
          +row + 13,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'B',
          'I',
        );

        const secondVisaFill = createStyleObject(
          +row + 14,
          {
            fill: {
              fgColor: {
                rgb: 'D9D9D9',
              },
            },
          },
          'B',
          'I',
        );

        const thirdVisa = createStyleObject(
          +row + 15,
          {
            font: {
              name: MAIN_FONT,
              sz: 9,
            },
            border: {
              bottom: { style: 'thin', color: { rgb: '000000' } },
            },
          },
          'B',
          'I',
        );

        const thirdVisaFill = createStyleObject(
          +row + 16,
          {
            fill: {
              fgColor: {
                rgb: 'D9D9D9',
              },
            },
          },
          'B',
          'I',
        );

        createDinamicStyles(responsiblesData, ws);
        createDinamicStyles(responsiblesHeader, ws);
        createDinamicStyles(optionalResponsiblesHeader, ws);
        createDinamicStyles(responsiblesCell, ws);
        createDinamicStyles(signerData, ws);
        createDinamicStyles(signerDataFill, ws);
        createDinamicStyles(opinionData, ws);
        createDinamicStyles(firstVisa, ws);
        createDinamicStyles(secondVisa, ws);
        createDinamicStyles(thirdVisa, ws);
        createDinamicStyles(firstVisaFill, ws);
        createDinamicStyles(secondVisaFill, ws);
        createDinamicStyles(thirdVisaFill, ws);
      }
    }
  });

  let rowIndex = 19;
  const merges: XLSX.Range[] | undefined = [];
  ws['!merges']?.forEach(elem => {
    if (elem.s.r < rowIndex) {
      merges.push(elem);
    }
  });

  for (let row = 0; row < rowIndex; row++) {
    charsIterate('A', 'Q', letter => {
      const cellAddress = letter + row;
      newWs[cellAddress] = ws[cellAddress];
    });
  }

  for (let row = rowIndex; row < rowLimit; row++) {
    charsIterate('A', 'Q', letter => {
      const cellAddress = letter + row;
      const newCellAddress = `${letter}${row + rowsToAdd}`;
      newWs[newCellAddress] = ws[cellAddress];
    });

    if (row >= rowIndex) {
      ws['!merges']?.forEach(elem => {
        if (elem.s.r === row || elem.e.r === row) {
          const newElem = { s: { c: elem.s.c, r: row + rowsToAdd }, e: { c: elem.e.c, r: row + rowsToAdd } };
          merges.push(newElem);
        }
      });
    }
  }

  newWs['!merges'] = merges;

  for (let user = 0; user < data.length; user++) {
    // Добавляем новую ячейку в новые данные
    prepareUserData({ user: data[user], rowIndex: rowIndex, worksheet: newWs, types: typesOvertime });
    rowIndex++;
  }
  newWs.D3 = { ...newWs.D3, v: docSignatory };
  XLSX.utils.book_append_sheet(workbook, newWs);
  XLSX.writeFile(workbook, 'updated_file.xlsx', { bookType: 'xlsx', bookSST: false, cellStyles: true });
};
